import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Spinner } from "reactstrap";
import toastr from "toastr";
import Spinners from "../../../components/Common/Spinner";
import {
  useGetClientBookingDetails,
  useStartClientSmartMatching,
} from "../../../api/booking";
import InviteFlowLayout from "./components/InviteFlowLayout";
import SmartMatchingAutoStartCountdown from "./components/SmartMatchingAutoStartCountdown";
import {
  getInviteFlowStepFromBooking,
} from "./inviteFlowStep";
import Step1SuggestedWorkers from "./step1/Step1SuggestedWorkers";
import Step2SmartMatching from "./step2/Step2SmartMatching";
import Step3Review from "./step3/Step3Review";
import dayjs from "dayjs";
import { useSocket } from "../../../providers/SocketProvider";
import { canAccessInviteWorkers } from "../clientBookingAccess";

function stripSmartMatchingZoneToken(v) {
  return String(v).replace(/\s+[A-Z]{2,5}$/, "");
}

/** When multiple shifts return a schedule, keep the earliest wall time. */
function earlierSmartMatchingWallTime(a, b) {
  if (!a) return b ?? null;
  if (!b) return a;
  const da = dayjs(stripSmartMatchingZoneToken(a));
  const db = dayjs(stripSmartMatchingZoneToken(b));
  if (!da.isValid()) return b;
  if (!db.isValid()) return a;
  return da.valueOf() <= db.valueOf() ? a : b;
}

/** `smartMatchingStartTime` + `ttlMinutes` → ISO instant for countdown target. */
function smartMatchingStartPlusTtlIso(rawStart, ttlMinutes) {
  if (!rawStart || !Number.isFinite(ttlMinutes)) return null;
  const base = dayjs(stripSmartMatchingZoneToken(rawStart));
  if (!base.isValid()) return null;
  return base.add(ttlMinutes, "minutes").toISOString();
}

const ClientInviteWorkers = () => {
  const { bookingSlug } = useParams();
  const navigate = useNavigate();
  const { socket, isConnected } = useSocket();
  const {
    data: bookingRes,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetClientBookingDetails(bookingSlug);

  useEffect(() => {
    if (isConnected) {
      socket.on("smart-matching-complete", async () => {
        toastr.success("Smart matching completed.");
        await refetch();
      });
    }
    return () => {
      if (isConnected) {
        socket.off("smart-matching-complete");
      }
    };
  }, [isConnected, socket, refetch]);

  const startSmartMatching = useStartClientSmartMatching();

  const booking = useMemo(
    () => bookingRes?.data?.data ?? bookingRes?.data ?? bookingRes,
    [bookingRes]
  );

  const inviteAccessDenied =
    !isLoading && !error && booking && !canAccessInviteWorkers(booking);

  useEffect(() => {
    if (!inviteAccessDenied || !bookingSlug) return;
    navigate(`/client/bookings/${bookingSlug}`, { replace: true });
  }, [inviteAccessDenied, bookingSlug, navigate]);

  const activeStep = useMemo(
    () => getInviteFlowStepFromBooking(booking),
    [booking]
  );

  /** When booking omits `smartMatchingStartTime`, Step 1 fills this from suggested-workers / session GET. */
  const [suggestedScheduleMeta, setSuggestedScheduleMeta] = useState(null);

  useEffect(() => {
    setSuggestedScheduleMeta(null);
  }, [bookingSlug]);

  useEffect(() => {
    if (booking?.smartMatchingStartTime) setSuggestedScheduleMeta(null);
  }, [booking?.smartMatchingStartTime]);

  const onSmartMatchingScheduleKnown = useCallback((meta) => {
    if (!meta?.smartMatchingStartTime) return;
    setSuggestedScheduleMeta((prev) => {
      const start = prev?.smartMatchingStartTime
        ? earlierSmartMatchingWallTime(
            prev.smartMatchingStartTime,
            meta.smartMatchingStartTime
          )
        : meta.smartMatchingStartTime;
      const ttl =
        meta.sessionTtlMinutes != null
          ? meta.sessionTtlMinutes
          : prev?.sessionTtlMinutes;
      return { smartMatchingStartTime: start, sessionTtlMinutes: ttl };
    });
  }, []);

  const effectiveSmartMatchingStart = useMemo(() => {
    const raw =
      booking?.smartMatchingStartTime ||
      suggestedScheduleMeta?.smartMatchingStartTime;

    const ttlFromBooking =
      typeof booking?.suggestedWorkersSessionTtlMinutes === "number"
        ? booking.suggestedWorkersSessionTtlMinutes
        : Number(booking?.suggestedWorkersSessionTtlMinutes);

    const ttlMin = Number.isFinite(ttlFromBooking)
      ? ttlFromBooking
      : suggestedScheduleMeta?.sessionTtlMinutes != null
      ? suggestedScheduleMeta.sessionTtlMinutes
      : NaN;

    return smartMatchingStartPlusTtlIso(raw, ttlMin);
  }, [
    booking?.smartMatchingStartTime,
    booking?.suggestedWorkersSessionTtlMinutes,
    suggestedScheduleMeta,
  ]);

  const handleSmartMatchingScheduleExpire = useCallback(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    document.title = "Invite workers | Client";
  }, []);

  useEffect(() => {
    document.title =
      activeStep === 1
        ? "Invite workers | Client"
        : activeStep === 2
        ? "Smart matching | Invite workers | Client"
        : "Review | Invite workers | Client";
  }, [activeStep]);

  // /** While smart matching runs, poll booking detail so we can move to step 3 when complete. */
  // useEffect(() => {
  //   if (activeStep !== 2) return;
  //   const id = setInterval(() => refetch(), 30000); // 30 seconds
  //   return () => clearInterval(id);
  // }, [activeStep, refetch]);

  if (isLoading && !booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Spinners setLoading={() => {}} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="danger">
            Could not load booking. {error?.message || ""}
          </Alert>
          <Button color="primary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Alert color="warning">Booking not found.</Alert>
        </div>
      </div>
    );
  }

  if (inviteAccessDenied) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Spinners setLoading={() => {}} />
        </div>
      </div>
    );
  }

  const handleRunSmartMatching = () => {
    if (!bookingSlug) return;
    startSmartMatching.mutate(bookingSlug, {
      onSuccess: async () => {
        await refetch();
        toastr.success("Smart matching started.");
      },
    });
  };

  return (
    <InviteFlowLayout
      booking={booking}
      bookingSlug={bookingSlug}
      activeStep={activeStep}
    >
      {activeStep === 1 && (
        <>
          {effectiveSmartMatchingStart && (
            <SmartMatchingAutoStartCountdown
              targetIso={effectiveSmartMatchingStart}
              onExpire={handleSmartMatchingScheduleExpire}
            />
          )}
          <div className="mb-4">
            <Button
              color="primary"
              className="w-100 py-3 fs-5"
              disabled={startSmartMatching.isPending}
              onClick={handleRunSmartMatching}
            >
              {startSmartMatching.isPending ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Starting…
                </>
              ) : (
                "Run Smart Matching"
              )}
            </Button>
          </div>
          <Step1SuggestedWorkers
            booking={booking}
            bookingSlug={bookingSlug}
            refetchBooking={refetch}
            onSmartMatchingScheduleKnown={onSmartMatchingScheduleKnown}
          />
        </>
      )}
      {activeStep === 2 && <Step2SmartMatching />}
      {activeStep === 3 && (
        <Step3Review booking={booking} bookingSlug={bookingSlug} refetchBooking={refetch} />
      )}
      {isFetching && !isLoading && (
        <div className="small text-muted mt-2">Refreshing booking…</div>
      )}
    </InviteFlowLayout>
  );
};

export default ClientInviteWorkers;
